import makeStyles from '@mui/styles/makeStyles';
import {LogOut, Target, User, Video} from "lucide-react";
import {capitalize, get, isEmpty} from "lodash";
import React from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {NavLink} from "react-router-dom";
import {hasActiveMentorship} from "business/matching";
import {openChatwoot} from "common/ChatwootHelper";
import {initials} from "common/helpers";
import {color, font, radius, type} from "common/tokens";
import ShimmerImage from "components/redesign/ShimmerImage";

const useStyles = makeStyles({
  menu: {
    width: 264,
  },
  identity: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '16px',
  },
  avatar: {
    ...type.meta,
    width: 40,
    height: 40,
    borderRadius: radius.PILL,
    background: color.PURPLE,
    color: color.ON_PURPLE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: radius.PILL,
  },
  who: {
    minWidth: 0,
  },
  name: {
    ...type.body,
    fontWeight: font.SEMIBOLD,
    color: color.TEXT,
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  role: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
    margin: 0,
  },
  divider: {
    height: 1,
    background: color.DIVIDER,
  },
  group: {
    padding: 8,
    display: 'flex',
    flexDirection: 'column',
  },
  item: {
    ...type.body,
    fontWeight: font.MEDIUM,
    appearance: 'none',
    background: 'none',
    border: 'none',
    textAlign: 'left',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 12px',
    borderRadius: 8,
    color: color.TEXT,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      background: color.SURFACE_MUTED,
    },
  },
  itemIcon: {
    color: color.TEXT_MUTED,
    display: 'flex',
    flexShrink: 0,
  },
  signOut: {
    color: color.DANGER,
    '& $itemIcon': {
      color: color.DANGER,
    },
  },
  legal: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px 10px',
    padding: '12px 16px',
  },
  legalLink: {
    ...type.chip,
    fontWeight: font.REGULAR,
    color: color.TEXT_SUBTLE,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
});

const AccountMenu = ({displayName, picture, role, onNavigate, onSignOut}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const matched = useSelector(state => hasActiveMentorship(get(state, 'auth.user.status')));

  const links = [
    {to: '/moreinfo', label: t('myProfile'), icon: <User size={16}/>},
    {to: '/moreinfo/faq', label: t('faqsAndWalks'), icon: <Video size={16}/>},
  ];

  const legal = [
    {to: '/moreinfo/privacyPolicy', label: t('privacyPolicy')},
    {to: '/moreinfo/termsOfService', label: t('termsOfService')},
    {to: '/moreinfo/codeofconduct', label: t('codeOfConduct')},
  ];

  return (
    <div className={classes.menu}>
      <div className={classes.identity}>
        <span className={classes.avatar}>
          {isEmpty(picture)
            ? initials(displayName)
            : <ShimmerImage src={picture} className={classes.avatarImage}/>}
        </span>
        <div className={classes.who}>
          <p className={classes.name}>{displayName}</p>
          {Boolean(role) && <p className={classes.role}>{capitalize(role)}</p>}
        </div>
      </div>

      <div className={classes.divider}/>

      <div className={classes.group}>
        {links.map(link => (
          <NavLink key={link.to} to={link.to} className={classes.item} onClick={onNavigate}>
            <span className={classes.itemIcon}>{link.icon}</span>
            {link.label}
          </NavLink>
        ))}
        {!matched && (
          <button
            type={'button'}
            className={classes.item}
            onClick={() => {
              openChatwoot();
              onNavigate();
            }}
          >
            <span className={classes.itemIcon}><Target size={16}/></span>
            {t('activeSupport')}
          </button>
        )}
      </div>

      <div className={classes.divider}/>

      <div className={classes.legal}>
        {legal.map(link => (
          <NavLink key={link.to} to={link.to} className={classes.legalLink} onClick={onNavigate}>
            {link.label}
          </NavLink>
        ))}
      </div>

      <div className={classes.divider}/>

      <div className={classes.group}>
        <button type={'button'} className={`${classes.item} ${classes.signOut}`} onClick={onSignOut}>
          <span className={classes.itemIcon}><LogOut size={16}/></span>
          {t('signOut')}
        </button>
      </div>
    </div>
  );
};

export default AccountMenu;
